import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import partnerService from '../services/partnerService';
import './PartnerList.css';

const PartnerList = () => {
  const [partners, setPartners] = useState([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partnerService.getAllPartners();
      setPartners(response.data.partners || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch partners');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, companyname) => {
    if (window.confirm(`Are you sure you want to delete partner "${companyname}"?`)) {
      try {
        await partnerService.deletePartner(id);
        fetchPartners();
      } catch (err) {
        alert('Failed to delete partner: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? partners.filter(p =>
        (p.companyname || '').toLowerCase().includes(q) ||
        (p.email       || '').toLowerCase().includes(q) ||
        (p.country     || '').toLowerCase().includes(q)
      )
    : partners;

  if (loading) return <div className="loading">Loading partners...</div>;

  return (
    <div className="partner-list-container">
      <div className="partner-list-header">
        <div>
          <h1>Partners</h1>
          <p className="page-subtitle">Manage partner organizations and their hotel chains</p>
        </div>
        <div className="partner-list-header-actions">
          <input
            className="partner-search-input"
            type="text"
            placeholder="Search by name, email or country…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn-primary" onClick={() => navigate('/partners/new')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create New Partner
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 6v4M10 14h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <div className="table-container">
        <table className="partners-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Company Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Country</th>
              <th>Currency</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="7" className="no-data">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" stroke="#d1d5db" strokeWidth="2"/>
                  <path d="M24 16v16M16 24h16" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p>{q ? 'No partners match your search' : 'No partners found'}</p>
                {!q && <p className="no-data-hint">Create your first partner to get started</p>}
              </td></tr>
            ) : (
              filtered.map(partner => (
                <tr
                  key={partner.partnerid}
                  className="clickable-row"
                  onClick={() => navigate(`/partners/${partner.partnerid}`)}
                  title="Click to view"
                >
                  <td><span className="id-badge">{partner.partnerid}</span></td>
                  <td className="company-name">{partner.companyname || '-'}</td>
                  <td>{partner.email || '-'}</td>
                  <td>{partner.phone || '-'}</td>
                  <td>{partner.country || '-'}</td>
                  <td><span className="currency-badge">{partner.currency || '-'}</span></td>
                  <td className="actions-cell">
                    <button
                      className="btn-action btn-edit-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/partners/${partner.partnerid}/edit`);
                      }}
                      title="Edit"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button
                      className="btn-action btn-delete-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(partner.partnerid, partner.companyname);
                      }}
                      title="Delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4h8l-.5 9a1 1 0 01-1 1h-5a1 1 0 01-1-1L4 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartnerList;
